import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
  effect,
} from "@angular/core";
import {
  CommandPaletteService,
  PaletteItem,
} from "@core/services/command-palette.service";
import { HotkeyService } from "@core/services/hotkey.service";
import { IconComponent } from '@shared/components/ui/icon/icon.component';
import { ModalOpenDirective } from "@shared/directives/modal-open.directive";

/**
 * ⌘K command palette modal (GAP-005).
 *
 * Mount once at the root (`app.component.html`). Visibility is driven by
 * `CommandPaletteService.isOpen` so any component can call `open()`.
 *
 * Keyboard:
 *   - ↑ / ↓        — move selection
 *   - Enter        — invoke selected
 *   - Esc          — close
 * Mouse:
 *   - click result — invoke
 *   - click backdrop — close
 */
@Component({
  selector: "app-command-palette",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, ModalOpenDirective],
  templateUrl: "./command-palette.component.html",
  styleUrls: ["./command-palette.component.scss"],
})
export class CommandPaletteComponent {
  private readonly palette = inject(CommandPaletteService);
  private readonly hotkeys = inject(HotkeyService);

  protected readonly isOpen = this.palette.isOpen;
  protected readonly query = this.palette.query;
  protected readonly results = this.palette.results;
  protected readonly modKey = this.hotkeys.modifierLabel();

  /** Highlighted result index (used by ↑/↓ navigation). */
  protected readonly selectedIndex = signal(0);

  /** Auto-clamp selection inside [0, results.length-1]. */
  protected readonly safeIndex = computed(() => {
    const count = this.results().length;
    if (count === 0) {
      return 0;
    }
    const idx = this.selectedIndex();
    if (idx >= count) {
      return count - 1;
    }
    if (idx < 0) {
      return 0;
    }
    return idx;
  });

  constructor() {
    // Native modal autofocus targets the search input; each open resets selection.
    effect(() => {
      if (this.isOpen()) {
        this.selectedIndex.set(0);
      }
    });
  }

  protected onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.palette.setQuery(target.value);
    this.selectedIndex.set(0);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (!this.isOpen()) {
      return;
    }
    if (
      event.isComposing ||
      (event.key !== "Escape" && !(event.target instanceof HTMLInputElement))
    ) {
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.move(-1);
        break;
      case "Enter":
        event.preventDefault();
        this.invokeSelected();
        break;
      case "Escape":
        event.preventDefault();
        this.palette.close();
        break;
    }
  }

  protected invokeItem(item: PaletteItem): void {
    this.palette.invoke(item);
  }

  protected invokeSelected(): void {
    const items = this.results();
    if (items.length === 0) {
      return;
    }
    const item = items[this.safeIndex()];
    if (item) {
      this.palette.invoke(item);
    }
  }

  // GAP-FE-PRIME-P2-SHARED · returns bare icon names consumed by
  // `<ui-icon [name]="iconForKind(item.kind)">` in the template.
  protected iconForKind(kind: PaletteItem["kind"]): string {
    switch (kind) {
      case "recent":
        return "history";
      case "symbol":
        return "dollar";
      case "action":
        return "bolt";
      case "route":
      default:
        return "arrow-right";
    }
  }

  protected trackById(_index: number, item: PaletteItem): string {
    return item.id;
  }

  protected close(): void {
    this.palette.close();
  }

  private move(delta: number): void {
    const count = this.results().length;
    if (count === 0) {
      return;
    }
    const next = (this.safeIndex() + delta + count) % count;
    this.selectedIndex.set(next);
  }
}
